import { yupResolver } from '@hookform/resolvers/yup'
import { ControlledTextField } from '@if/mui'
import { Alert, Box, Paper, Typography } from '@mui/material'
import ConfirmActionButton from '@src/shared/components/ConfirmActionButton'
import { useCleanupByContent } from '@src/tools/error-tracker/api/admin-api'
import { useToast } from '@src/shared/hooks/useToast'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

interface FormData {
  content: string
}

const CleanupPage = () => {
  const { t } = useTranslation()
  const { showSuccess, showError, ToastComponent } = useToast()
  const [cleanup, isPending, isError, resetMutation] = useCleanupByContent()

  const cleanupSchema = yup.object({
    content: yup.string().required(t('cleanup.contentRequired')).min(1, t('cleanup.contentRequired')),
  })

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      content: '',
    },
    resolver: yupResolver(cleanupSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    resetMutation()
  }, [resetMutation])

  const onSubmit = async (formData: FormData) => {
    try {
      const result = await cleanup({ contains: formData.content })
      showSuccess(t('cleanup.successMessage', { count: result.deletedCount }))
      reset() // Clear the form after successful cleanup
    } catch (err) {
      showError(t('cleanup.errorMessage'))
      reportErrorToConsole(err)
    }
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        {t('cleanup.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('cleanup.description')}
      </Typography>

      <Paper elevation={2} sx={{ p: 3, maxWidth: 600 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 3 }}>
            <ControlledTextField
              name="content"
              control={control}
              fullWidth
              label={t('cleanup.contentLabel')}
              placeholder={t('cleanup.contentPlaceholder')}
              helperText={t('cleanup.contentHelperText')}
              disabled={isPending}
              multiline
              rows={3}
            />
          </Box>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('cleanup.errorMessage')}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ConfirmActionButton
              onConfirm={handleSubmit(onSubmit)}
              loading={isPending}
              isError={isError}
              disabled={!isValid || isPending}
              confirmText={t('cleanup.confirmText')}
              variant="contained"
              color="primary"
              sx={{ minWidth: 200 }}
            >
              {t('cleanup.deleteButton')}
            </ConfirmActionButton>
          </Box>
        </form>
      </Paper>

      <ToastComponent />
    </Box>
  )
}

export default CleanupPage
